import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Box, IconButton, Divider } from '@mui/material';
import { Bold, Italic, List, ListOrdered } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  editable?: boolean;
}

export function RichTextEditor({ value, onChange, placeholder, editable = true }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value || '',
    editable,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:focus-within': {
          borderColor: 'primary.main',
          borderWidth: 2,
        },
      }}
    >
      {editable && (
        <>
          <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} color={editor.isActive('bold') ? 'primary' : 'default'}>
              <Bold size={16} />
            </IconButton>
            <IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} color={editor.isActive('italic') ? 'primary' : 'default'}>
              <Italic size={16} />
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} color={editor.isActive('bulletList') ? 'primary' : 'default'}>
              <List size={16} />
            </IconButton>
            <IconButton size="small" onClick={() => editor.chain().focus().toggleOrderedList().run()} color={editor.isActive('orderedList') ? 'primary' : 'default'}>
              <ListOrdered size={16} />
            </IconButton>
          </Box>
        </>
      )}
      <Box
        sx={{
          p: 1.5,
          minHeight: 100,
          '& .tiptap': {
            outline: 'none',
            '& p': { m: 0, mb: 1 },
            '& p:last-child': { mb: 0 },
            '& ul, & ol': { m: 0, pl: 2.5 },
            '& li': { mb: 0.5 },
          },
          '& .tiptap p.is-editor-empty:first-child::before': {
            content: `"${placeholder || 'Enter text...'}"`,
            color: 'text.disabled',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
